const DB_NAME = 'timelining-debug-v2'
const DB_VERSION = 1
const BLOB_STORE = 'blobs'

export type DebugBlobKind = 'screenshot' | 'attachment'

export type DebugStoredBlob = {
  id: string
  sessionId: string
  kind: DebugBlobKind
  mimeType: string
  size: number
  createdAt: string
  filename: string
  blob: Blob
}

let dbPromise: Promise<IDBDatabase> | null = null

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available in this environment.'))
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        const store = db.createObjectStore(BLOB_STORE, { keyPath: 'id' })
        store.createIndex('sessionId', 'sessionId', { unique: false })
      }
    }
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed.'))
    request.onsuccess = () => resolve(request.result)
  })
  return dbPromise
}

const withStore = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> => {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(BLOB_STORE, mode)
    const store = tx.objectStore(BLOB_STORE)
    const request = fn(store)
    if (request instanceof IDBRequest) {
      request.onsuccess = () => resolve(request.result as T)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB op failed.'))
    } else {
      request
        .then((value) => {
          tx.oncomplete = () => resolve(value)
          tx.onerror = () => reject(tx.error ?? new Error('IndexedDB tx failed.'))
        })
        .catch(reject)
    }
  })
}

export const putDebugBlob = async (record: DebugStoredBlob): Promise<void> => {
  await withStore('readwrite', (store) => store.put(record))
}

export const getDebugBlob = async (id: string): Promise<DebugStoredBlob | undefined> => {
  return withStore<DebugStoredBlob | undefined>('readonly', (store) => store.get(id) as IDBRequest<DebugStoredBlob | undefined>)
}

export const listDebugBlobsForSession = async (sessionId: string): Promise<DebugStoredBlob[]> => {
  const db = await openDb()
  return new Promise<DebugStoredBlob[]>((resolve, reject) => {
    const tx = db.transaction(BLOB_STORE, 'readonly')
    const store = tx.objectStore(BLOB_STORE)
    const index = store.index('sessionId')
    const request = index.getAll(IDBKeyRange.only(sessionId))
    request.onsuccess = () => resolve((request.result as DebugStoredBlob[]) ?? [])
    request.onerror = () => reject(request.error ?? new Error('IndexedDB getAll failed.'))
  })
}

export const deleteDebugBlobsForSession = async (sessionId: string): Promise<void> => {
  const db = await openDb()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(BLOB_STORE, 'readwrite')
    const store = tx.objectStore(BLOB_STORE)
    const index = store.index('sessionId')
    const cursorReq = index.openCursor(IDBKeyRange.only(sessionId))
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result
      if (cursor) {
        cursor.delete()
        cursor.continue()
      }
    }
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB clear failed.'))
  })
}

export const deleteDebugBlob = async (id: string): Promise<void> => {
  await withStore('readwrite', (store) => store.delete(id))
}
